import CompactSelect from '../../components/common/CompactSelect';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { useAlbumDetail, useAlbumDetailBySlug } from './hooks/useAlbumDetail';
import { useAlbumReviews } from './hooks/useAlbumReviews';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import PageLayout from '../../components/layout/PageLayout';
import AlbumLoadState from './components/album/AlbumLoadState';
import { ReviewRow } from './components/user-review/UserReviewSection';
import {
  MemberAlbumSummary,
  MemberDistribution,
  RatingMember,
  OwnMemberReview,
} from './components/user-review/MemberReviewListParts';
import {
  memberReviewSections,
  sortMemberReviews,
} from './components/user-review/memberReviewListModel';
import { reviewRoutePaths } from '../../utils/reviewRoutePaths';
import baseline from '../../app-ui/baseline';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import './MemberReviews.css';
import { ReviewDraftProvider } from './components/detail/ReviewDraftProtection';

const EMPTY = [];
export default function UserReviews() {
  const memory = useBrowseSession('album-member-reviews');
  const user = useUserStore((s) => s.user);
  const { locale } = useWebLocale();
  return (
    <ReviewDraftProvider
      key={`${memory.location.key}:${memory.entry.version}`}
      en={locale === 'en'}
    >
      <MemberReviewsPage user={user} {...memory} />
    </ReviewDraftProvider>
  );
}
function MemberReviewsPage({ user, entry, returning }) {
  const params = useParams(),
    location = useLocation(),
    navigate = useNavigate();
  const routes = reviewRoutePaths(location.pathname, location.search);
  const slug = useAlbumDetailBySlug(params.albumSlug),
    direct = useAlbumDetail(params.albumSource, params.sourceId);
  const result = params.albumSlug ? slug : direct;
  const album = result.data;
  const source = params.albumSlug ? slug.albumSource : params.albumSource,
    id = params.albumSlug ? slug.sourceId : params.sourceId;
  const reviewsQuery = useAlbumReviews(source, id);
  const records = reviewsQuery.data ?? EMPTY;
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme();
  const en = locale === 'en';
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const [view, setView] = useState(() => entry.memberView ?? { tab: 'reviews', sort: 'latest' });
  const buttons = useRef([]),
    swipe = useRef(null),
    focused = useRef(false);
  const { reviews, ratings } = useMemo(() => memberReviewSections(records), [records]);
  const own = user ? records.find((r) => String(r.user?.id) === String(user.id)) : null;
  const mine = own?.content?.trim() ? own : null;
  const publicReviews = useMemo(
    () => sortMemberReviews(reviews, view.sort, mine?.id),
    [reviews, view.sort, mine?.id],
  );
  const ready = Boolean(album && reviewsQuery.data !== undefined);
  const loading = result.isLoading || reviewsQuery.isLoading;
  const error = result.error || reviewsQuery.error;
  const position = useBrowsePosition({ entry, returning, ready });
  useEffect(() => {
    if (entry.current()) entry.memberView = view;
  }, [entry, view]);
  useEffect(() => {
    const focusId = new URLSearchParams(location.search).get('focusReviewId');
    if (!ready || !focusId || focused.current || (returning && entry.y !== undefined)) return;
    if (view.tab !== 'reviews') {
      setView((v) => ({ ...v, tab: 'reviews' }));
      return;
    }
    const row = Array.from(document.querySelectorAll('[data-review-id]')).find(
      (e) => e.dataset.reviewId === focusId,
    );
    if (row) {
      setView((v) => ({ ...v, tab: 'reviews' }));
      row.scrollIntoView({ block: 'start' });
      focused.current = true;
    }
  }, [ready, location.search, view.tab, returning, entry]);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_USERREVIEWS,
      contentType: CONTENT_TYPE.ALBUM,
      contentId: id,
      contentName: album?.title,
    },
    { ready: Boolean(id) },
  );
  const retry = () => {
    if (result.error || !album) void (result.retry ?? result.refetch)?.();
    if (id) void reviewsQuery.refetch();
  };
  const tabs = ['reviews', 'ratings'];
  const changeTab = (tab) => setView((v) => ({ ...v, tab }));
  const keyTab = (event, index) => {
    const next = { ArrowRight: (index + 1) % 2, ArrowLeft: (index + 1) % 2, Home: 0, End: 1 }[
      event.key
    ];
    if (next !== undefined) {
      event.preventDefault();
      changeTab(tabs[next]);
      buttons.current[next]?.focus();
    }
  };
  return (
    <PageLayout paddingTop={0}>
      <div
        {...position}
        className="pp-member-reviews"
        style={{
          '--member-background': colors.background,
          '--member-section-gap': `${baseline.tokens.spacing.lg}px`,
          '--member-grid-row-gap': `${baseline.tokens.spacing.xl}px`,
          '--member-text': colors.text,
          '--member-secondary': colors.textSecondary,
          '--member-body': colors.reviewBody,
          '--member-divider': colors.divider,
          '--member-muted': colors.surfaceMuted,
        }}
      >
        <div className="pp-member-toolbar">
          <button
            type="button"
            aria-label={en ? 'Back to album' : '앨범으로 돌아가기'}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(routes.album))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{en ? 'Member Reviews & Ratings' : '회원 리뷰·평가'}</h1>
        </div>
        <AlbumLoadState
          album={ready ? album : null}
          loading={loading}
          error={error}
          onRetry={retry}
        >
          {ready && (
            <>
              <MemberAlbumSummary album={album} to={routes.album} en={en} />
              <div className="pp-member-divider" />
              {records.length ? (
                <MemberDistribution
                  records={records}
                  average={Number(album.averageRating) || 0}
                  reviewCount={reviews.length}
                  en={en}
                />
              ) : (
                <p className="pp-member-empty">
                  {en ? 'No rating statistics yet.' : '아직 별점 통계가 없습니다.'}
                </p>
              )}
              <div className="pp-member-divider" />
              <div
                role="tablist"
                aria-label={en ? 'Reviews and Ratings' : '리뷰와 평가'}
                className="pp-member-tabs"
              >
                {tabs.map((tab, i) => (
                  <button
                    key={tab}
                    ref={(el) => {
                      buttons.current[i] = el;
                    }}
                    id={`member-${tab}-tab`}
                    role="tab"
                    aria-selected={view.tab === tab}
                    aria-controls={`member-${tab}-panel`}
                    tabIndex={view.tab === tab ? 0 : -1}
                    onClick={() => changeTab(tab)}
                    onKeyDown={(e) => keyTab(e, i)}
                  >
                    {i === 0 ? (en ? 'Reviews' : '리뷰') : en ? 'Ratings' : '평가'}{' '}
                    {i === 0 ? reviews.length : ratings.length}
                  </button>
                ))}
              </div>
              <div
                onTouchStart={(e) => {
                  swipe.current = null;
                  if (
                    e.touches.length === 1 &&
                    e.touches[0].clientX >= 16 &&
                    e.touches[0].clientX <= window.innerWidth - 16 &&
                    !e.target.closest('a,button,input,select')
                  )
                    swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                }}
                onTouchEnd={(e) => {
                  const from = swipe.current;
                  swipe.current = null;
                  const to = e.changedTouches[0];
                  if (
                    from &&
                    to &&
                    Math.abs(to.clientX - from.x) >
                      Math.max(40, e.currentTarget.clientWidth * 0.12) &&
                    Math.abs(to.clientX - from.x) > Math.abs(to.clientY - from.y) * 2
                  )
                    changeTab(to.clientX < from.x ? 'ratings' : 'reviews');
                }}
              >
                <section
                  id="member-reviews-panel"
                  role="tabpanel"
                  aria-labelledby="member-reviews-tab"
                  hidden={view.tab !== 'reviews'}
                  tabIndex={0}
                >
                  <OwnMemberReview
                    key={album.id || album.sourceId}
                    album={album}
                    record={own}
                    basePath={routes.list}
                    en={en}
                    user={user}
                  />
                  <div className="pp-member-divider" />
                  <div className="pp-member-heading">
                    <div>
                      <h2>{en ? 'Member Reviews' : '회원 리뷰'}</h2>
                      <span>
                        {reviews.length}
                        {en ? ' reviews' : '개'}
                      </span>
                    </div>
                    <CompactSelect
                      label={en ? 'Sort reviews' : '리뷰 정렬'}
                      value={view.sort}
                      onChange={(e) => setView((v) => ({ ...v, sort: e.target.value }))}
                      options={[
                        ['latest', en ? 'Latest' : '최신순'],
                        ['empathy', en ? 'Most Likes' : '좋아요순'],
                        ['ratingHigh', en ? 'Highest Rating' : '높은 평점순'],
                        ['ratingLow', en ? 'Lowest Rating' : '낮은 평점순'],
                      ]}
                    />
                  </div>
                  <div className="pp-member-full-list">
                    {publicReviews.map((review) => (
                      <ReviewRow
                        key={review.id}
                        review={review}
                        basePath={routes.list}
                        en={en}
                        full
                      />
                    ))}
                  </div>
                  {!publicReviews.length && (
                    <div className="pp-member-empty">
                      <p>
                        {mine
                          ? en
                            ? 'No other member reviews yet.'
                            : '아직 다른 회원 리뷰가 없습니다.'
                          : en
                            ? 'No member reviews yet.'
                            : '아직 회원 리뷰가 없습니다.'}
                      </p>
                      <p>
                        {en
                          ? 'New reviews will appear here.'
                          : '새로운 리뷰가 등록되면 이곳에서 볼 수 있어요.'}
                      </p>
                    </div>
                  )}
                </section>
                <section
                  id="member-ratings-panel"
                  role="tabpanel"
                  aria-labelledby="member-ratings-tab"
                  hidden={view.tab !== 'ratings'}
                  tabIndex={0}
                >
                  {ratings.length ? (
                    <div className="pp-member-rating-grid">
                      {ratings.map((review) => (
                        <RatingMember key={review.id} review={review} en={en} />
                      ))}
                    </div>
                  ) : (
                    <div className="pp-member-empty">
                      <p>
                        {en ? 'No rating-only members yet.' : '아직 별점만 남긴 회원이 없어요.'}
                      </p>
                      <p>
                        {en
                          ? 'Members who leave a rating without a review will appear here.'
                          : '리뷰 없이 별점만 남긴 회원이 여기에 표시됩니다.'}
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </AlbumLoadState>
      </div>
    </PageLayout>
  );
}
